// The tenant's tag list and tags on contacts.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './client'
import type { components } from './generated/schema'

export type Tag = components['schemas']['Tag']

const tagsKey = ['tags'] as const

export function useTags() {
  return useQuery({
    queryKey: tagsKey,
    queryFn: async (): Promise<Tag[]> => unwrap(await api.GET('/tags')),
  })
}

// A tag change can alter any conversation row, so lists and open
// conversations refresh too.
function useRefreshTags() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: tagsKey })
    void qc.invalidateQueries({ queryKey: ['conversations'] })
    void qc.invalidateQueries({ queryKey: ['conversation'] })
    void qc.invalidateQueries({ queryKey: ['contacts'] })
  }
}

export function useCreateTag() {
  const refresh = useRefreshTags()
  return useMutation({
    mutationFn: async (name: string) => unwrap(await api.POST('/tags', { body: { name } })),
    onSuccess: refresh,
  })
}

export function useRenameTag() {
  const refresh = useRefreshTags()
  return useMutation({
    mutationFn: async (input: { id: string; name: string }) =>
      unwrap(await api.PATCH('/tags/{tagId}', { params: { path: { tagId: input.id } }, body: { name: input.name } })),
    onSuccess: refresh,
  })
}

export function useDeleteTag() {
  const refresh = useRefreshTags()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/tags/{tagId}', { params: { path: { tagId: id } } })
      if (!res.response.ok) unwrap(res)
    },
    onSuccess: refresh,
  })
}

export function useContactTag(contactId: string) {
  const refresh = useRefreshTags()
  return useMutation({
    mutationFn: async (input: { tagId: string; on: boolean }) => {
      const params = { params: { path: { contactId, tagId: input.tagId } } }
      const res = input.on
        ? await api.PUT('/contacts/{contactId}/tags/{tagId}', params)
        : await api.DELETE('/contacts/{contactId}/tags/{tagId}', params)
      if (!res.response.ok) unwrap(res)
    },
    onSuccess: refresh,
  })
}
